import { useMemo } from "react";
import styled from "styled-components/macro";
import { Process } from "types/types";
import { useFocusRange } from "utils/customHooks/genericHooks";
import { usePnlSummaryChartSectionsForProcess } from "utils/customHooks/pnlChartHooks";
import { useSelector } from "store/useSelector";
import { addShiftlyRollupsIntoSingleRollup } from "../../TrendChart/trendChartUtils";
import { calcPnlChartCategories } from "../../PnlSummaryChart/calcPnlChartCategories";
import { PnlSummaryChartContent } from "components/Dashboard/PnlSummaryChart/PnlSummaryChartContent/PnlSummaryChartContent";

type TopThreeLossesChartProps = {
  process: Process;
  isTagSelected?: boolean;
  selectedTag?: string | null;
  isShiftSelected?: boolean;
  selectedShift?: string | null;
  visible: boolean;
};

export function TopThreeLossesChart({
  process,
  isTagSelected,
  selectedTag,
  isShiftSelected,
  selectedShift,
  visible,
}: TopThreeLossesChartProps) {
  const focusRange = useFocusRange();

  // Get all rollups data for shift filtering
  const allRollupsMetricTrendChartBarsArrays = useSelector(
    state => state.legacy.allRollupsMetricTrendChartBarsArrays
  );

  // Get hidden PNL chart categories
  const hiddenPnlChartCategories = useSelector(
    state => state.legacy.hiddenPnlChartCategories
  );

  // Get the standard PNL sections for reference and reuse when not filtering
  const pnlSummaryChartSectionsForProcess =
    usePnlSummaryChartSectionsForProcess(process.id);

  // Generate PNL chart sections based on filter state
  const pnlChartSections = useMemo(() => {
    // If shift is selected, create shift-filtered rollup
    if (isShiftSelected && selectedShift && focusRange) {
      // Get rollups for this process
      const rollupsForProcess =
        allRollupsMetricTrendChartBarsArrays[process.id];

      if (!rollupsForProcess || rollupsForProcess.length === 0) {
        return [];
      }

      // Filter rollups for just this shift
      const rollupsForShift = rollupsForProcess.filter(
        ({ shift }) => shift?.name === selectedShift
      );

      if (rollupsForShift.length === 0) {
        return [];
      }

      try {
        // Add all the rollups of that shift together over the focus range
        const summedShiftlyRollup = addShiftlyRollupsIntoSingleRollup({
          rollupsArr: rollupsForShift,
          focusRange,
          shiftName: selectedShift,
        });

        // Calculate PNL chart categories from the shift-summed rollup
        return calcPnlChartCategories(
          summedShiftlyRollup,
          hiddenPnlChartCategories
        );
      } catch (e) {
        console.error("Error creating shift-filtered PNL chart", e);
        return [];
      }
    }

    // If no shift selected, use standard PNL chart data
    return pnlSummaryChartSectionsForProcess;
  }, [
    isShiftSelected,
    selectedShift,
    focusRange,
    process.id,
    allRollupsMetricTrendChartBarsArrays,
    hiddenPnlChartCategories,
    pnlSummaryChartSectionsForProcess,
  ]);

  // Apply tag filtering if needed
  const filteredSections = useMemo(() => {
    if (!pnlChartSections || pnlChartSections.length === 0) {
      return [];
    }

    // If tag filtering is applied, only show bars for the selected tag
    if (isTagSelected && selectedTag) {
      return pnlChartSections
        .map(section => ({
          ...section,
          bars: section.bars.filter(bar => bar.title === selectedTag),
          // Important: recalculate section time and percent based on filtered bars
          time: section.bars
            .filter(bar => bar.title === selectedTag)
            .reduce((sum, bar) => sum + (bar.time || 0), 0),
          percent: section.bars
            .filter(bar => bar.title === selectedTag)
            .reduce((sum, bar) => sum + (bar.percent || 0), 0),
        }))
        .filter(section => section.bars.length > 0);
    }

    return pnlChartSections;
  }, [isTagSelected, selectedTag, pnlChartSections]);

  // Get only the top 3 losses
  const topThreeLosses = useMemo(() => {
    if (!filteredSections || filteredSections.length === 0) {
      return [];
    }

    // First, flatten all bars from all sections
    const allBars = filteredSections.flatMap(section => section.bars);

    // Sort by time (or percent) descending to get the top losses
    const sortedBars = [...allBars].sort(
      (a, b) => (b.time || 0) - (a.time || 0)
    );

    // Take the top 3
    const top3Bars = sortedBars.slice(0, 3);

    // If no bars, return empty array
    if (top3Bars.length === 0) {
      return [];
    }

    // Create a single section with the top 3 bars that matches PnlChartSectionType
    return [
      {
        title: "Top 3 Losses", // Changed from "Top Losses" to "Top 3 Losses"
        time: top3Bars.reduce((sum, bar) => sum + (bar.time || 0), 0),
        count: top3Bars.length, // Add count property
        percent: top3Bars.reduce((sum, bar) => sum + (bar.percent || 0), 0),
        bars: top3Bars,
      },
    ];
  }, [filteredSections]);

  if (!focusRange || topThreeLosses.length === 0) {
    return null;
  }

  return (
    <TopThreeLossesChartContainer $visible={visible}>
      {/* Removed redundant title - section header will show "Top 3 Losses" */}
      <PnlSummaryChartContent
        pnlBreakdownChartCategorySectionsArr={topThreeLosses}
        isInsideMachineBreakdownChart={true}
        timeRange={focusRange}
        disableAnimation={true}
      />
    </TopThreeLossesChartContainer>
  );
}

const TopThreeLossesChartContainer = styled.div<{ $visible: boolean }>`
  position: relative;
  padding: 0 16px;
  margin-top: 8px;
  margin-bottom: 16px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.4s ease-in-out; /* Increased duration for more noticeable fade */
  /* No height or overflow transitions for instant appearance */
  height: auto;
  overflow: visible;
  will-change: opacity; /* Optimize for opacity animations */
`;
